import {InputSwitch, InputSwitchChangeEvent} from 'primereact/inputswitch';
import React, {ChangeEvent, Component} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators, Dispatch} from 'redux';

import {Button} from 'primereact/button';
import {Column} from 'primereact/column';
import {Dialog} from 'primereact/dialog';
import {Splitter, SplitterPanel} from 'primereact/splitter';
import {TabPanel, TabView, TabViewTabChangeEvent} from 'primereact/tabview';
import {Toolbar} from 'primereact/toolbar';
import {TreeTable} from 'primereact/treetable';
import TreeNode from 'primereact/treenode';

import Joi from '~/shared/src/utilities/joiExtended';
import {__} from '~/shared/src/utilities/messageCatalog';
import {panelSchema} from '~/shared/src/validationSchemes/panel';

import {setActiveTabIndex, clearState} from '~/frontend/src/actions/panelPropsDialog';
import hidePanelPropsDialog from '~/frontend/src/actions/panelManager/hidePanelPropsDialog';
import {ValidationErrorItem, ValidationResult} from 'joi';
import {StoreState} from '~/frontend/src/store';
import {
    panelClasses,
    PanelType,
    PanelClass
} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {getPanelIndex} from '~/frontend/src/components/base/page/PageTabPanel/utilities';
import {PanelManagerState} from '~/frontend/src/reducers/panelManager';
import {PanelPropsDialogProps} from '~/frontend/src/reducers/panelPropsDialog';

const Actions = { setActiveTabIndex, hidePanelPropsDialog };

const defaultProps = {
    /**
     * Is the dialog is visible.
     */
    visible: false as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {
    /**
     * The key of the panel to show the panelProps for.
     */
    panelKey : string;
};

type StateProps = PanelPropsDialogProps & {

    /**
     * The panelManager state for this component.
     *
     * Will be mapped from global state.
     */
    panelManager? : PanelManagerState;
};

type ActionProps = {
    setActiveTabIndex? : Function;
    hidePanelPropsDialog? : Function;
};

type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    panelPropsSchema : JoiSchema;
    panelPropsSchemaMsgIdPrefix : string;
    panelDefaults : boolean;
    panelPropsToValidate : string;
    panelPropsValidationResult : string | JSX.Element;
};

type SwitchObject = {
    schema? : JoiSchemaDescription;
    validValues? : any[];
    invalidValues? : any[];
    otherwise? : boolean;
};

type Switch = {
    [ key : string ] : SwitchObject[]
};

type JoiSchemaDescription = {
    type? : string;
    default? : any;
    required? : boolean;
    label? : string;
    description? : string;
    examples? : any;
    validValues? : any[];//IterableIterator<any>;
    invalidValues? : any[];//IterableIterator<any>;
    switch? : Switch;
    alternatives? : { [ key : string ] : JoiSchemaDescription };
    items? : { [ key : string ] : JoiSchemaDescription };
    keys? : { [ key : string ] : JoiSchemaDescription };
    keyDependencies? : {
        type : any;
        keys : any;
        index? : number;
        children? : JoiSchemaDescriptionData[];
        output? : boolean;
    }[]
};

type Alternative = {
    schema? : JoiSchema;
    switch? : Alternative[];
    is? : JoiSchema;
    then? : JoiSchema;
    ref? : JoiSchema;
    otherwise? : JoiSchema;
};

type JoiSchemaDescriptionData = {
    key : string;
    data : {
        name : string;
        type? : string;
        rowType : string;
        required? : boolean;
        default? : any;
        validValues? : any[];
        invalidValues? : any[];
        description : string;
        validConditionValues? : any[];
        invalidConditionValues? : any[];
        otherwise? : boolean;
    },
    children? : JoiSchemaDescriptionData[]
};


/**
 * PanelProps dialog
 */
class PanelPropsDialog extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    // panel manager config
    static onClosePanel = clearState;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        const { panelPropsSchema, panelPropsSchemaMsgIdPrefix } = this.getPanelPropsSchemaInfo( this.props.panelKey );

        this.state = {
            panelPropsSchema,
            panelPropsSchemaMsgIdPrefix: `${panelPropsSchemaMsgIdPrefix}.panelProps`,
            panelDefaults              : true,
            panelPropsToValidate       : '',
            panelPropsValidationResult : ''
        };

        // bind event handlers
        this.onDialogHide = this.onDialogHide.bind( this );
        this.onTabViewTabChange = this.onTabViewTabChange.bind( this );
        this.onValidateButtonClick = this.onValidateButtonClick.bind( this );
        this.onPanelPropsToValidateTextareaChange = this.onPanelPropsToValidateTextareaChange.bind( this );
        this.panelPropsDescriptionTreeTableNameRenderer = this.panelPropsDescriptionTreeTableNameRenderer.bind( this );
        this.panelPropsDescriptionTreeTableDescriptionRenderer = this.panelPropsDescriptionTreeTableDescriptionRenderer.bind( this );
        this.panelPropsDescriptionTreeTableDefaultRenderer = this.panelPropsDescriptionTreeTableDefaultRenderer.bind( this );
        this.onShowDefaultsInputSwitchChange = this.onShowDefaultsInputSwitchChange.bind( this );
    }

    /**
     * Handler for dialog onHide.
     *
     * @private
     */
    onDialogHide() {
        this.props.hidePanelPropsDialog?.( this.props.panelKey );
    }

    /**
     * Handler for TabView tabChange.
     *
     * @private
     */
    onTabViewTabChange( event : TabViewTabChangeEvent ) {
        this.props.setActiveTabIndex?.( this.props.panelKey, event.index );
    }

    /**
     * Handler for validate Button click.
     *
     * @private
     */
    onValidateButtonClick() {
        this.validatePanelProps();
    }

    /**
     * Handler for panelPropsToValidate Textarea change.
     *
     * @param {SyntheticInputEvent} event
     *
     * @private
     */
    onPanelPropsToValidateTextareaChange( event : ChangeEvent<HTMLTextAreaElement> ) {
        this.setState( {
            panelPropsToValidate      : event.target.value,
            panelPropsValidationResult: ''
        } );
    }

    /**
     * Handler for showDefaults InputSwitch change.
     *
     * @param event
     *
     * @private
     */
    onShowDefaultsInputSwitchChange( event : InputSwitchChangeEvent ) {
        this.setState( { panelDefaults: event.value } );
    }

    /**
     * Returns the component and the config for the given panelKey.
     *
     * @param {String} panelKey
     *
     * @return {{ PanelComponent: PanelClass | null, panelConfig: PanelProps | null }}
     */
    getPanelInfo( panelKey : string ) : { PanelComponent : PanelClass | null, panelConfig : PanelProps | null } {
        const panelManager   = this.props.panelManager,
              panelIndex     = getPanelIndex( panelKey ),
              panelConfig    = panelIndex !== undefined
                  ? panelManager?.openPanels[ panelIndex ] ?? null
                  : null,
              PanelComponent = panelConfig?.panelProps
                  ? panelClasses[ panelConfig?.panelProps.panelType as PanelType ] as unknown as PanelClass
                  : null;

        return { PanelComponent, panelConfig };
    }

    /**
     * Returns the panelPropsSchema and the panelPropsSchemaMsgIdPrefix for the given panelKey.
     *
     * @param {String} panelKey
     * @param {Boolean} withPanelSchema
     *
     * @return {{panelPropsSchema: Joi.schema, panelPropsSchemaMsgIdPrefix: String}}
     */
    getPanelPropsSchemaInfo( panelKey : string, withPanelSchema : boolean = true ) {
        const { PanelComponent } = this.getPanelInfo( panelKey );

        let panelPropsSchema,
            panelPropsSchemaMsgIdPrefix = 'YN.base.common';

        // get panelPropsSchema
        if ( PanelComponent?.panelPropsSchema ) {
            if ( withPanelSchema ) {
                panelPropsSchema = Joi.object().concat( panelSchema ).concat( PanelComponent.panelPropsSchema );
            }
            else {
                panelPropsSchema = PanelComponent.panelPropsSchema;
            }

            if ( PanelComponent.panelPropsSchemaMsgIdPrefix ) {
                panelPropsSchemaMsgIdPrefix = PanelComponent.panelPropsSchemaMsgIdPrefix;
            }
        }

        return { panelPropsSchema, panelPropsSchemaMsgIdPrefix };
    }

    /**
     * Returns the current panelProps for the given panelKey.
     *
     * @param {String} panelKey
     * @param {Boolean} withDefaults
     *
     * @return {ValidationResult}
     */
    getPanelProps( panelKey : string, withDefaults : boolean = true ) : ValidationResult {
        const { PanelComponent, panelConfig } = this.getPanelInfo( panelKey );

        let retVal = {} as ValidationResult;

        if ( PanelComponent?.panelPropsSchema && Joi.isSchema( PanelComponent.panelPropsSchema ) ) {
            const panelProps = panelConfig?.panelProps,
                  panelType  = panelProps?.panelType;

            // get panelProps defaults
            let panelPropsDefaults = PanelComponent.panelPropsSchema.validate(
                {},
                {
                    abortEarly: false
                }
            );
            panelPropsDefaults = panelPropsDefaults.value;

            // build full current panelProps
            retVal = Object.assign(
                {},
                {
                    panelType
                },
                panelPropsDefaults,
                panelProps
            );

            // validate with schema to get only the parameters described in the schema
            retVal = PanelComponent.panelPropsSchema.validate(
                retVal,
                {
                    abortEarly  : false,
                    stripUnknown: {
                        arrays : true,
                        objects: true
                    },
                    convert     : false,
                    noDefaults  : !withDefaults
                }
            );

            retVal = retVal.value;

            // remove panelPropsDefaults from panelProps if
            if ( !withDefaults ) {
                const removeDefaultsRecursive = ( recursivePanelProps : PanelProps, recursivePanelPropsDefaults : PanelProps ) => {
                    Object.keys( recursivePanelProps ).forEach( key => {
                        if ( typeof recursivePanelProps[ key ] === 'object' && recursivePanelPropsDefaults[ key ] !== undefined ) {
                            recursivePanelProps[ key ] = removeDefaultsRecursive(
                                recursivePanelProps[ key ],
                                recursivePanelPropsDefaults[ key ]
                            );

                            if ( !Object.keys( recursivePanelProps[ key ] ).length ) {
                                delete recursivePanelProps[ key ];
                            }
                        }
                        else if ( recursivePanelPropsDefaults[ key ] !== undefined && recursivePanelProps[ key ] === recursivePanelPropsDefaults[ key ] ) {
                            delete recursivePanelProps[ key ];
                        }
                    } );

                    return recursivePanelProps;
                };

                retVal = removeDefaultsRecursive( retVal, panelPropsDefaults ) as ValidationResult;
            }

            // TODO: sort as in panelSchema + panelPropsSchema
        }

        return retVal;
    }

    /**
     * Converts the Joi.schema from the component this dialog belongs to to a more readable and processable form.
     *
     * @param {Joi.schema} schema
     *
     * @return {Object}
     */
    getSchemaDescription( schema : JoiSchema ) : JoiSchemaDescription {
        const retVal : JoiSchemaDescription = {};

        if ( Joi.isSchema( schema ) ) {

            /* eslint-disable no-underscore-dangle */

            // type
            retVal.type = schema.type;

            // required
            if ( schema._flags.presence === 'required' ) {
                retVal.required = true;
            }

            // default value
            if ( schema._flags.default !== undefined ) {
                retVal.default = schema._flags.default;
            }

            // description
            if ( schema._flags.description ) {
                retVal.description = schema._flags.description;
            }

            // examples
            if ( schema.$_terms.examples?.length ) {
                retVal.examples = schema.$_terms.examples;
            }

            // label
            if ( schema._flags.label ) {
                retVal.label = schema._flags.label;
            }

            // valid values
            if ( schema._valids?._values?.size ) {
                retVal.validValues = schema._valids.values();
            }

            // invalid values
            if ( schema._invalids?._values?.size ) {
                retVal.invalidValues = schema._invalids.values();
            }

            // examples
            if ( schema.$_terms.examples?.length ) {
                retVal.examples = schema.$_terms.examples;
            }


            // when -> conditional
            if ( schema.$_terms.whens?.length && schema.type !== 'link' ) {
                retVal.type = 'conditional';
                retVal.switch = {};

                schema.$_terms.whens.forEach( ( when : any ) => {
                    retVal.switch![ when.ref.key ] = [];

                    const isDescription            = this.getSchemaDescription( when.is ),
                          switchObj : SwitchObject = {};

                    if ( isDescription.validValues?.length ) {
                        switchObj.validValues = isDescription.validValues;
                    }

                    if ( isDescription.invalidValues?.length ) {
                        switchObj.invalidValues = isDescription.invalidValues;
                    }

                    if ( Object.keys( switchObj ).length ) {
                        switchObj.schema = this.getSchemaDescription( when.then );
                        retVal.switch?.[ when.ref.key ].push( switchObj );
                    }

                    if ( when.otherwise ) {
                        retVal.switch?.[ when.ref.key ].push( {
                            otherwise: true,
                            schema   : this.getSchemaDescription( when.otherwise )
                        } );
                    }
                } );
            }

            // alternatives
            if ( schema.type === 'alternatives' ) {
                retVal.alternatives = {};
                retVal.switch = {};

                schema.$_terms.matches.forEach( ( alternative : Alternative, index : number ) => {

                    // alternatives
                    if ( alternative.schema && retVal.alternatives ) {
                        retVal.alternatives[ String( index + 1 ) ] = this.getSchemaDescription( alternative.schema );
                    }

                    // alternatives -> conditional
                    else if ( retVal.switch && alternative.ref && ( alternative.switch || alternative.is ) ) {
                        retVal.type = 'conditional';
                        retVal.switch[ alternative.ref.key ] = [];

                        let switchCases : Alternative[] = [];
                        if ( alternative.is ) {
                            switchCases.push( alternative );
                        }
                        else {
                            switchCases = alternative.switch!;
                        }

                        switchCases.forEach( ( switchCase ) => {
                            const isDescription            = this.getSchemaDescription( switchCase.is! ),
                                  switchObj : SwitchObject = {};

                            if ( isDescription.validValues?.length ) {
                                switchObj.validValues = isDescription.validValues;
                            }

                            if ( isDescription.invalidValues?.length ) {
                                switchObj.invalidValues = isDescription.invalidValues;
                            }

                            if ( Object.keys( switchObj ).length ) {
                                switchObj.schema = this.getSchemaDescription( switchCase.then! );
                                retVal.switch?.[ alternative.ref!.key ].push( switchObj );
                            }

                            if ( switchCase.otherwise ) {
                                retVal.switch?.[ alternative.ref!.key ].push( {
                                    otherwise: true,
                                    schema   : this.getSchemaDescription( switchCase.otherwise )
                                } );
                            }
                        } );
                    }
                } );
            }
            else {
                // array items
                if ( schema.type === 'array' && schema.$_terms.items ) {
                    retVal.items = {};

                    schema.$_terms.items.forEach( function( itemSchema : JoiSchema, index : number ) {
                        retVal.items![ String( index + 1 ) ] = this.getSchemaDescription( itemSchema );
                    }, this );
                }

                // object keys
                if ( schema.type === 'object' && schema._ids._byKey.size ) {
                    retVal.keys = {};

                    if ( schema.$_terms.dependencies?.length ) {
                        retVal.keyDependencies = [];
                        schema.$_terms.dependencies.forEach( ( dependency : JoiSchema ) => {
                            retVal.keyDependencies!.push( {
                                type: dependency.rel,
                                keys: dependency.paths
                            } );
                        } );
                    }

                    for ( const entry of schema._ids._byKey.values() ) {
                        if ( entry.id && entry.schema ) {
                            retVal.keys[ entry.id ] = this.getSchemaDescription( entry.schema );
                        }
                    }
                }
            }

            /* eslint-enable no-underscore-dangle */
        }

        return retVal;
    }

    /**
     * Extracts the data from an schema description to an multidimensional array structure for using in grids array store.
     *
     * @param {JoiSchemaDescription} schemaDescription
     * @param {String} schemaMsgIdPrefix
     * @param {String} key
     * @param {String} parentKey
     *
     * @return {JoiSchemaDescriptionData}
     */
    getDataFromSchemaDescription( schemaDescription : JoiSchemaDescription, schemaMsgIdPrefix : string, key : undefined, parentKey : undefined ) : JoiSchemaDescriptionData[];
    getDataFromSchemaDescription( schemaDescription : JoiSchemaDescription, schemaMsgIdPrefix : string, key : string, parentKey : string ) : JoiSchemaDescriptionData;
    getDataFromSchemaDescription( schemaDescription : JoiSchemaDescription, schemaMsgIdPrefix : string, key : string = '', parentKey : string = '' ) : JoiSchemaDescriptionData | JoiSchemaDescriptionData[] {
        const nextParentKey                     = parentKey
                  ? `${parentKey}.${key}`
                  : key
                      ? key
                      : '',
              currentDataKey                    = parentKey
                  ? `${parentKey}.${key}`
                  : key,
              retVal : JoiSchemaDescriptionData = {
                  key     : `${currentDataKey}`,
                  data    : {
                      name         : key,
                      type         : schemaDescription.type,
                      rowType      : 'content',
                      required     : schemaDescription.required,
                      default      : schemaDescription.default,
                      validValues  : schemaDescription.validValues,
                      invalidValues: schemaDescription.invalidValues,
                      description  : schemaDescription.description ?? ''
                  },
                  children: []
              };

        let children : JoiSchemaDescriptionData[],
            msgId,
            msg,
            name,
            description;

        // localize name for array items
        if ( /^item(.[0-9]+)?$/.test( retVal.data.name ) ) {
            msgId = 'YN.base.panelProps.data.name.item';
            msg = __( msgId );

            if ( msg !== msgId ) {
                retVal.data.name = msg;
            }
        }
        // localize description
        if ( retVal.data.description ) {
            msgId = `${schemaMsgIdPrefix}.${currentDataKey}`;
            msg = __( msgId );

            if ( msg !== msgId ) {
                retVal.data.description = msg;
            }
        }

        if ( schemaDescription.alternatives && Object.keys( schemaDescription.alternatives ).length ) {
            children = [];
            Object.keys( schemaDescription.alternatives ).forEach( subKey => {
                children.push( this.getDataFromSchemaDescription( schemaDescription.alternatives![ subKey ], schemaMsgIdPrefix, `${subKey}`, nextParentKey ) );
            } );

            // localize name
            msgId = `${schemaMsgIdPrefix}.alternatives`;
            msg = __( msgId );
            name = msg !== msgId
                ? msg
                : __( 'YN.base.panelProps.data.name.alternatives' );

            retVal.children!.push( {
                key     : `${currentDataKey}.alternatives`,
                data    : {
                    name       : name,
                    rowType    : 'header',
                    description: ''
                },
                children: children
            } );
        }

        if ( schemaDescription.switch && Object.keys( schemaDescription.switch ).length ) {
            Object.keys( schemaDescription.switch ).forEach( refKey => {
                retVal.children!.push( {
                    key : `${currentDataKey}.switch.${refKey}`,
                    data: {
                        name       : String( refKey ),
                        rowType    : 'conditionHeader',
                        description: ''
                    }
                } );

                schemaDescription.switch?.[ refKey ].forEach( ( caseObj, caseIndex ) => {
                    const child = this.getDataFromSchemaDescription( caseObj.schema!, schemaMsgIdPrefix, caseIndex + '', nextParentKey );

                    child.data.rowType = 'condition';
                    if ( caseObj.validValues ) {
                        child.data.validConditionValues = caseObj.validValues;
                    }
                    if ( caseObj.invalidValues ) {
                        child.data.invalidConditionValues = caseObj.invalidValues;
                    }
                    if ( caseObj.otherwise ) {
                        child.data.otherwise = caseObj.otherwise;
                    }
                    retVal.children!.push( child );
                } );

            } );
        }

        if ( schemaDescription.items ) {
            if ( Object.keys( schemaDescription.items ).length !== 1 ) {
                // localize name
                msgId = `${schemaMsgIdPrefix}.items`;
                msg = __( msgId );
                name = msg !== msgId
                    ? msg
                    : __( 'YN.base.panelProps.data.name.items' );

                retVal.children!.push( {
                    key : `${currentDataKey}.items`,
                    data: {
                        name       : name,
                        rowType    : 'itemsHeader',
                        description: ''
                    }
                } );
            }

            Object.keys( schemaDescription.items ).forEach( subKey => {
                const nextKey = Object.keys( schemaDescription.items! ).length === 1
                          ? 'item'
                          : `item.${subKey}`,
                      child   = this.getDataFromSchemaDescription( schemaDescription.items![ subKey ], schemaMsgIdPrefix, nextKey, nextParentKey );

                child.data.rowType = 'itemHeader';

                retVal.children!.push( child );
            } );

        }

        if ( schemaDescription.keys ) {
            Object.keys( schemaDescription.keys ).forEach( subKey => {
                let keyHasDependency = false;

                // if key has dependency like xor, ... add to that dependency children
                if ( schemaDescription.keyDependencies ) {
                    schemaDescription.keyDependencies.forEach( ( keyDependency, index ) => {
                        keyDependency.index = index;

                        if ( keyDependency.keys.indexOf( subKey ) !== -1 ) {
                            keyHasDependency = true;

                            if ( !keyDependency.children ) {
                                keyDependency.children = [];
                            }
                            keyDependency.children.push( this.getDataFromSchemaDescription( schemaDescription.keys![ subKey ], schemaMsgIdPrefix, String( subKey ), nextParentKey ) )
                        }

                        if ( keyDependency.children && !keyDependency.output && keyDependency.keys.length === keyDependency.children.length ) {
                            keyDependency.output = true;

                            // localize name
                            msgId = `${schemaMsgIdPrefix}.dependency.${keyDependency.type}`;
                            msg = __( msgId );
                            name = msg !== msgId
                                ? msg
                                : __( 'YN.base.panelProps.data.name.keyDependency' );

                            // localize description
                            msgId = `${schemaMsgIdPrefix}.dependency.${keyDependency.type}`;
                            msg = __( msgId );
                            description = msg !== msgId
                                ? msg
                                : __( `YN.base.panelProps.data.description.keyDependency.${String( keyDependency.type ).toUpperCase()}` );

                            retVal.children?.push( {
                                key     : `${currentDataKey}.dependency.${index}`,
                                data    : {
                                    name       : name,
                                    rowType    : 'header',
                                    type       : String( keyDependency.type ).toUpperCase(),
                                    description: description
                                },
                                children: keyDependency.children
                            } );
                        }
                    } );
                }

                // key has not dependency add as normal child
                if ( !keyHasDependency ) {
                    retVal.children?.push( this.getDataFromSchemaDescription( schemaDescription.keys![ subKey ], schemaMsgIdPrefix, String( subKey ), nextParentKey ) );
                }
            } );
        }

        if ( key === '' ) {
            // TODO: check how to handle array or not as retVal???
            return retVal.children!;
        }
        else {
            return retVal;
        }
    }

    validatePanelProps() {
        const validationResult = {
            message: '' as string | JSX.Element,
            details: [] as string[]
        };

        let panelPropsToValidate,
            validation;

        if ( this.state.panelPropsToValidate ) {
            try {
                panelPropsToValidate = JSON.parse( this.state.panelPropsToValidate );
            }
            catch ( e ) {
                validationResult.message = <span className="yn-error">{__( 'YN.base.panelProps.validation.error.noValidJSON.text' )}</span>;

                if ( e instanceof Error ) {
                    validationResult.details.push( e.message );
                }
            }

            if ( !validationResult.message ) {
                validation = this.state.panelPropsSchema.validate(
                    panelPropsToValidate,
                    {
                        abortEarly: false,
                        convert   : false
                    }
                );

                if ( validation.error ) {
                    validationResult.message =
                        <span className="yn-error">{__( 'YN.base.panelProps.validation.error.validationFails.text' )}</span>;
                    validation.error.details.forEach( function( detail : ValidationErrorItem ) {
                        validationResult.details.push( detail.message );
                    } );
                }
                else {
                    validationResult.message = <span className="yn-ok">{__( 'YN.base.panelProps.validation.success.text' )}</span>;
                }
            }

            this.setState( {
                panelPropsValidationResult: <>
                    {validationResult.message}
                    {validationResult.details.length
                        ? <>
                            <br/><br/>Error details:<br/>
                            <ul className="yn-error-details">
                                {validationResult.details.map( ( detail, index ) => {
                                    const detailKey  = detail.replace( /^"([^"]+)"(.*)$/, '$1' ),
                                          detailText = detail.replace( /^"([^"]+)"(.*)$/, '$2' );
                                    return <li key={`errorDetail_${index}`}><strong>{detailKey}</strong>{detailText}</li>;
                                } )}
                            </ul>
                        </>
                        : ''
                    }
                </>
            } );
        }
    }

    /**
     * Gets the panel config description TreeTable.
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getPanelPropsDescriptionTreeTable() {
        const schemaDescription = this.getSchemaDescription( this.state.panelPropsSchema ),
              data              = this.getDataFromSchemaDescription( schemaDescription, this.state.panelPropsSchemaMsgIdPrefix, undefined, undefined );

        return <>
            <TreeTable value={data as TreeNode.TreeNode[]} scrollable scrollHeight="100%" className="X-p-treetable-striped">
                <Column
                    field="name"
                    header={__( 'YN.base.panelProps.column.name' )}
                    body={this.panelPropsDescriptionTreeTableNameRenderer}
                    style={{ width: '150px' }}
                    expander
                />
                <Column
                    field="description"
                    header={__( 'YN.base.panelProps.column.description' )}
                    body={this.panelPropsDescriptionTreeTableDescriptionRenderer}
                    style={{ width: '400px' }}
                />
                <Column
                    field="default"
                    header={__( 'YN.base.panelProps.column.default' )}
                    body={this.panelPropsDescriptionTreeTableDefaultRenderer}
                    style={{ width: '100px' }}
                />
            </TreeTable>
        </>;
    }

    /**
     * Renderer for the name column of the panelProps description TreeTable.
     *
     * @param {Object} nodeData
     *
     * @return {JSX.Element[]}
     *
     * @private
     */
    panelPropsDescriptionTreeTableNameRenderer( nodeData : any ) : ( JSX.Element | string )[] {
        const rowData = nodeData.data;

        const retVal : ( JSX.Element | string )[] = [];
        let valuesPrefix,
            values;

        switch ( rowData.rowType ) {
            case 'itemHeader':
            case 'itemsHeader':
                retVal.push( rowData.name );
                break;

            case 'conditionHeader':
                retVal.push( 'if ' );
                retVal.push( <span key="name" className="yn-bold">{rowData.name}</span> );
                retVal.push( ' is' );
                break;

            case 'condition':
                if ( rowData.otherwise ) {
                    retVal.push( 'otherwise' );
                }
                else if ( rowData.validConditionValues ) {
                    valuesPrefix = 'set to';
                    values = rowData.validConditionValues;
                }
                else if ( rowData.invalidConditionValues ) {
                    valuesPrefix = 'not set to';
                    values = rowData.invalidConditionValues;
                }

                if ( values ) {
                    retVal.push(
                        <>
                            {valuesPrefix}&nbsp;
                            {values.map( ( value : any, index : number ) => {
                                return <>
                                    {index === values.length - 1 && values.length > 1
                                        ? ' or '
                                        : ''}
                                    <span key={value} className="yn-item">{JSON.stringify( value )}</span>
                                </>;
                            } )}
                        </>
                    );
                }
                break;

            default:
                retVal.push( <span key="name" className="yn-bold">{rowData.name}</span> );
                break;
        }

        if ( rowData.required ) {
            retVal.push( <span key="required" className="yn-required">*</span> );
        }

        return retVal;
    }

    /**
     * Renderer for the description column of the panelProps description TreeTable.
     *
     * @param {Object} nodeData
     *
     * @return {JSX.Element}
     *
     * @private
     */
    panelPropsDescriptionTreeTableDescriptionRenderer( nodeData : any ) : JSX.Element {
        const rowData = nodeData.data;

        let type : JSX.Element | string = '';

        if ( rowData.validValues ) {
            if ( Array.isArray( rowData.validValues ) && rowData.validValues.length ) {
                type = <>
                    {rowData.validValues.map( ( value : any ) => {
                        return <span key={value} className="yn-item">{JSON.stringify( value )}</span>;
                    } )}
                </>;
            }
            else if ( Object.keys( rowData.validValues ).length ) {
                type = <>
                    {Object.keys( rowData.validValues ).map( ( key, index ) => {
                        const is        = <>
                                  {rowData.validValues[ key ].is.map( ( value : any, vIndex : number ) => {
                                      return <span key={`is_${index}_${vIndex}`} className="yn-item">{JSON.stringify( value )}</span>;
                                  } )}
                              </>,
                              then      = <>
                                  {rowData.validValues[ key ].then.map( ( value : any, vIndex : number ) => {
                                      return <span key={`then_${index}_${vIndex}`} className="yn-item">{JSON.stringify( value )}</span>;
                                  } )}
                              </>,
                              otherwise = <>
                                  {rowData.validValues[ key ].otherwise.map( ( value : any, vIndex : number ) => {
                                      return <span key={`otherwise_${index}_${vIndex}`}
                                                   className="yn-item">{JSON.stringify( value )}</span>;
                                  } )}
                              </>;

                        return <span key={`vV_${index}`}>
                            if <strong>{key}</strong> is set to {is} then {then}<br/>
                            otherwise {otherwise}
                        </span>;
                    } )}
                </>;
            }
        }
        else if ( rowData.type ) {
            type = <>
                <span className="yn-item">{rowData.type}</span>
            </>;
        }

        return <>
            {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
            <span key="description" dangerouslySetInnerHTML={{ __html: rowData.description }}/>
            {rowData.description
                ? <><br/><br/></>
                : ''}
            {type}
        </>;
    }

    /**
     * Renderer for the description column of the panelProps description TreeTable.
     *
     * @param {Object} nodeData
     *
     * @return {JSX.Element}
     *
     * @private
     */
    panelPropsDescriptionTreeTableDefaultRenderer( nodeData : any ) : JSX.Element | undefined {
        const rowData = nodeData.data;

        if ( rowData.default !== undefined ) {
            return <span className="yn-item">{JSON.stringify( rowData.default )}</span>;
        }
        else if ( rowData.rowType !== 'conditionHeader' ) {
            return <span>-</span>;
        }
    }

    /**
     * Gets the currently set panel configuration from the component this dialog belongs to.
     *
     * @return {string}
     *
     * @private
     */
    getPanelPropsCurrentTextArea() {
        const panelProps = this.getPanelProps( this.props.panelKey, this.state.panelDefaults );

        // format panelProps for output
        const strPanelProps = JSON.stringify( panelProps, null, 4 );

        const leftComponents = [
            <InputSwitch key="showDefaultsButton" inputId="showDefaults" checked={this.state.panelDefaults}
                         onChange={this.onShowDefaultsInputSwitchChange}/>,
            <label key="showDefaultsButtonLabel" htmlFor="showDefaults">{__( 'YN.base.panelProps.showDefaults' )}</label>
        ];

        return <>
            <Toolbar
                left={leftComponents}
            />
            <textarea
                className="yn-flex-layout-child yn-code-textarea"
                value={strPanelProps}
                onChange={() => {
                }}
            />
        </>;
    }


    /**
     * Gets the panelProps validation panel.
     *
     * @return {JSX.Element}
     *
     * @private
     */
    getPanelPropsValidationPanel() {
        const leftComponents = [
            <Button
                key="validateButton"
                onClick={this.onValidateButtonClick}

                icon="pi pi-check"
                className={`p-button-rounded p-button-outlined`}
            />
        ];

        return <>
            <Toolbar
                left={leftComponents}
            />
            <Splitter
                className="yn-flex-layout-child yn-content"
                style={{ zIndex: 4 }}
            >
                <SplitterPanel
                    size={50}
                    className="yn-flex-layout-parent"
                >
                    <textarea
                        className="yn-flex-layout-child yn-code-textarea"
                        value={this.state.panelPropsToValidate}
                        onChange={this.onPanelPropsToValidateTextareaChange}
                    />
                </SplitterPanel>
                <SplitterPanel
                    size={50}
                    className="yn-flex-layout-parent"
                >
                    <div
                        className="yn-flex-layout-child yn-border"
                        style={{ padding: '1rem 0.75rem' }}
                    >
                        {this.state.panelPropsValidationResult}
                    </div>
                </SplitterPanel>
            </Splitter>
        </>;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const { visible } = this.props;

        // avoid any processing if not visible
        if ( visible ) {
            return <Dialog
                header={__( 'YN.base.panelProps' )}
                visible={visible}
                onHide={this.onDialogHide}
                closable={true}
                className="yn-flex-layout-parent"
                contentClassName="yn-flex-layout-parent"
                maskClassName="yn-dialog-panel-modal"
                appendTo="self"
                style={{ width: '40%', maxWidth: '80%', height: '90%' }}
            >
                <TabView
                    activeIndex={this.props.activeTabIndex ?? 0}
                    onTabChange={this.onTabViewTabChange}
                    className="yn-flex-layout-parent"
                >
                    <TabPanel
                        key="panelPropsDescription"
                        header={__( 'YN.base.panelProps.description' )}
                        contentClassName="yn-flex-layout-parent"
                    >
                        {this.getPanelPropsDescriptionTreeTable()}
                    </TabPanel>
                    <TabPanel
                        key="panelPropsCurrent"
                        header={__( 'YN.base.panelProps.current' )}
                        contentClassName="yn-flex-layout-parent"
                    >
                        {this.getPanelPropsCurrentTextArea()}
                    </TabPanel>
                    <TabPanel
                        key="panelPropsValidate"
                        header={__( 'YN.base.panelProps.validate' )}
                        contentClassName="yn-flex-layout-parent"
                    >
                        {this.getPanelPropsValidationPanel()}
                    </TabPanel>
                </TabView>
            </Dialog>;
        }
        else {
            return <></>;
        }
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        panelManager: state.panelManager
    };

    // get state for this instance
    if ( ownProps.panelKey && state.panelPropsDialog && state.panelPropsDialog[ ownProps.panelKey ] ) {
        Object.keys( state.panelPropsDialog[ ownProps.panelKey ] ).forEach( ( key ) => {
            retVal[ key ] = state.panelPropsDialog[ ownProps.panelKey ][ key ];
        } );
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( PanelPropsDialog );
